import { useCallback, useEffect, useRef, useState } from 'react';

interface UseLongClickOptions {
  duration?: number; // Duration in milliseconds
  onLongClick?: (event: React.MouseEvent | React.TouchEvent) => void;
  onShortClick?: (event: React.MouseEvent | React.TouchEvent) => void;
}

export const useLongClick = ({
  duration = 1000,
  onLongClick,
  onShortClick,
}: UseLongClickOptions = {}) => {
  const [isLongClick, setIsLongClick] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isMouseDownRef = useRef(false);

  const handleMouseDown = useCallback(
    (event: React.MouseEvent | React.TouchEvent) => {
      isMouseDownRef.current = true;
      setIsLongClick(false);

      timerRef.current = setTimeout(() => {
        if (isMouseDownRef.current) {
          setIsLongClick(true);
          onLongClick?.(event);
        }
      }, duration);
    },
    [duration, onLongClick],
  );

  const handleMouseUp = useCallback(
    (event: React.MouseEvent | React.TouchEvent) => {
      isMouseDownRef.current = false;

      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }

      if (!isLongClick) {
        onShortClick?.(event);
      }
    },
    [isLongClick, onShortClick],
  );

  const handleMouseLeave = useCallback(() => {
    isMouseDownRef.current = false;

    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };
  }, []);

  return {
    isLongClick,
    longClickProps: {
      onMouseDown: handleMouseDown,
      onMouseUp: handleMouseUp,
      onMouseLeave: handleMouseLeave,
      onTouchStart: handleMouseDown,
      onTouchEnd: handleMouseUp,
    },
  };
};
